// src/components/AdminPanel.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaSignOutAlt, FaGuitar, FaPlus, FaEdit, FaTrash, 
  FaImage, FaChartBar, FaHome, FaCheck, 
  FaTimes, FaStar, FaBox, FaDollarSign, FaSpinner,
  FaSave, FaCamera, FaList, FaTachometerAlt, FaCog,
  FaUser, FaShoppingCart, FaTag, FaBars, FaTimes as FaTimesIcon
} from "react-icons/fa";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import GuitarManagement from "./GuitarManagement";

// Logo import et
import logo from "../assets/logo.png";

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({
    totalGuitars: 0,
    inStockGuitars: 0,
    featuredGuitars: 0,
    averagePrice: 0
  });
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Ekran boyutunu kontrol et
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Auth durumunu dinle
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        navigate("/admin/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // İstatistikleri yükle
  useEffect(() => {
    if (activeTab === "dashboard") {
      loadStats();
    }
  }, [activeTab]);

  const loadStats = () => {
    const q = query(collection(db, "guitars"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const guitars = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      const totalGuitars = guitars.length;
      const inStockGuitars = guitars.filter(g => g.stock > 0).length;
      const featuredGuitars = guitars.filter(g => g.featured).length;
      const totalPrice = guitars.reduce((sum, g) => sum + (g.price || 0), 0);
      const averagePrice = totalGuitars > 0 ? totalPrice / totalGuitars : 0;
      
      setStats({
        totalGuitars,
        inStockGuitars,
        featuredGuitars,
        averagePrice: parseFloat(averagePrice.toFixed(2))
      });
    });

    return () => unsubscribe();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Çıkış hatası:", error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: FaTachometerAlt },
    { id: "guitars", label: "Gitar Yönetimi", icon: FaGuitar },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-sm border-b border-gray-700/50 z-40 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg bg-gray-800/50 border border-gray-700/50 text-gray-300 hover:text-white"
            >
              {sidebarOpen ? <FaTimesIcon size={20} /> : <FaBars size={20} />}
            </button>
            <div className="flex items-center gap-2">
              {/* Logo resmini kullan */}
              <div className="w-50 h-8 flex items-center justify-center">
                <img 
                  src={logo} 
                  alt="Neo Guitar Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:text-white border border-red-500/30"
          >
            <FaSignOutAlt size={18} />
          </button>
        </div>
      </div>

      {/* Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden fixed inset-0 bg-black/50 z-30"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : (isMobile ? "-100%" : 0),
          width: isMobile ? "280px" : "256px"
        }}
        className="fixed left-0 top-0 h-full bg-gradient-to-b from-gray-800/95 to-gray-900/95 backdrop-blur-sm border-r border-gray-700/50 z-40 lg:z-0 overflow-y-auto"
      >
        <div className="p-6">
          {/* Desktop Logo */}
          <div className="hidden lg:flex items-center gap-3 mb-8">
            {/* Logo resmini kullan */}
            <div className="w-60 h-10 flex items-center justify-center">
              <img 
                src={logo} 
                alt="Neo Guitar Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Admin Panel</p>
            </div>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    activeTab === item.id
                      ? "bg-gradient-to-r from-blue-500/20 to-gray-500/20 text-blue-400 border border-blue-500/30"
                      : "text-gray-400 hover:text-white hover:bg-gray-700/50"
                  }`}
                >
                  <Icon />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-700/50">
          <div className="space-y-4">
            <Link
              to="/"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-gray-700 to-gray-800 text-gray-300 hover:text-white rounded-xl transition-colors"
              onClick={() => isMobile && setSidebarOpen(false)}
            >
              <FaHome />
              Siteye Dön
            </Link>
            
            {!isMobile && (
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-red-500/10 to-pink-500/10 text-red-400 hover:text-white rounded-xl border border-red-500/30 hover:border-red-500/50 transition-all"
              >
                <FaSignOutAlt />
                Çıkış Yap
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className={`pt-16 lg:pt-0 lg:ml-64 min-h-screen transition-all duration-300 ${isMobile ? 'p-4' : 'p-8'}`}>
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl lg:text-3xl font-bold text-white">
              {activeTab === "dashboard" && "Dashboard"}
              {activeTab === "guitars" && "Gitar Yönetimi"}
            </h1>
          </div>
          <p className="text-gray-400 text-sm lg:text-base">Neo Guitar Store Yönetim Paneli</p>
        </div>

        {/* Dashboard */}
        {activeTab === "dashboard" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6 lg:space-y-8"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 backdrop-blur-sm rounded-xl lg:rounded-2xl border border-blue-500/20 p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-400 text-xs lg:text-sm">Toplam Gitar</p>
                    <p className="text-2xl lg:text-3xl font-bold text-white mt-1 lg:mt-2">
                      {stats.totalGuitars}
                    </p>
                  </div>
                  <FaGuitar className="text-blue-400" size={20} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 backdrop-blur-sm rounded-xl lg:rounded-2xl border border-green-500/20 p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 text-xs lg:text-sm">Stoktaki Gitar</p>
                    <p className="text-2xl lg:text-3xl font-bold text-white mt-1 lg:mt-2">
                      {stats.inStockGuitars}
                    </p>
                  </div>
                  <FaBox className="text-green-400" size={20} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 backdrop-blur-sm rounded-xl lg:rounded-2xl border border-amber-500/20 p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-400 text-xs lg:text-sm">Öne Çıkan Gitar</p>
                    <p className="text-2xl lg:text-3xl font-bold text-white mt-1 lg:mt-2">
                      {stats.featuredGuitars}
                    </p>
                  </div>
                  <FaStar className="text-amber-400" size={20} />
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 backdrop-blur-sm rounded-xl lg:rounded-2xl border border-purple-500/20 p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-400 text-xs lg:text-sm">Ortalama Fiyat</p>
                    <p className="text-2xl lg:text-3xl font-bold text-white mt-1 lg:mt-2">
                      {stats.averagePrice ? `${stats.averagePrice.toLocaleString()} ₺` : "0 ₺"}
                    </p>
                  </div>
                  <FaDollarSign className="text-purple-400" size={20} />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              <button
                onClick={() => handleTabChange("guitars")}
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl lg:rounded-2xl border border-gray-700/50 p-4 lg:p-6 hover:border-blue-500/30 transition-all text-left"
              >
                <div className="flex items-center gap-3 mb-3 lg:mb-4">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 bg-gradient-to-r from-blue-500/20 to-blue-600/20 rounded-lg lg:rounded-xl flex items-center justify-center">
                    <FaPlus className="text-blue-400" size={18} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-sm lg:text-base">Yeni Gitar Ekle</h3>
                    <p className="text-gray-400 text-xs lg:text-sm">Mağazaya yeni gitar ekleyin</p>
                  </div>
                </div>
              </button>
            </div>

            {/* User Info */}
            {user && (
              <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-xl lg:rounded-2xl border border-gray-700/50 p-4 lg:p-6">
                <h3 className="text-lg font-bold text-white mb-3 lg:mb-4">Kullanıcı Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-4">
                  <div>
                    <p className="text-gray-400 text-xs lg:text-sm">Email</p>
                    <p className="text-white text-sm lg:text-base truncate">{user.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs lg:text-sm">Son Giriş</p>
                    <p className="text-white text-sm lg:text-base">
                      {user.metadata?.lastSignInTime 
                        ? new Date(user.metadata.lastSignInTime).toLocaleString('tr-TR')
                        : 'Bilinmiyor'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Gitar */}
        {activeTab === "guitars" && <GuitarManagement />}
      </div>
    </div>
  );
};

export default AdminPanel;